// The admin-side preset catalog (spec §4): presets expand to options at
// edit time; the server and widget only ever see options. Keys/icons/values
// must stay inside surveys-core's BUILTIN_ICONS and the 2..=10 option rule.
import type { ChoiceOption } from "@/api/types";

export interface ScalePreset {
    key: string;
    label: string;
    options: ChoiceOption[];
}

const option = (key: string, icon: string, value: number): ChoiceOption => ({ key, icon, value });

export const SCALE_PRESETS: ScalePreset[] = [
    {
        key: "smiley-4",
        label: "Smileys (4) — kiosk standard, no neutral",
        options: [
            option("angry", "smiley-angry", 1),
            option("sad", "smiley-sad", 2),
            option("happy", "smiley-happy", 3),
            option("delighted", "smiley-delighted", 4),
        ],
    },
    {
        key: "smiley-5",
        label: "Smileys (5) — with neutral",
        options: [
            option("angry", "smiley-angry", 1),
            option("sad", "smiley-sad", 2),
            option("neutral", "smiley-neutral", 3),
            option("happy", "smiley-happy", 4),
            option("delighted", "smiley-delighted", 5),
        ],
    },
    {
        key: "smiley-3",
        label: "Smileys (3) — quick pulse",
        options: [
            option("sad", "smiley-sad", 1),
            option("neutral", "smiley-neutral", 2),
            option("happy", "smiley-happy", 3),
        ],
    },
    {
        key: "stars-5",
        label: "Stars (5)",
        options: [1, 2, 3, 4, 5].map((n) => ({ key: `star-${n}`, icon: "star", label: String(n), value: n })),
    },
    {
        key: "thumbs-2",
        label: "Thumbs (2)",
        options: [option("down", "thumb-down", 0), option("up", "thumb-up", 1)],
    },
];

// Excluded until the server's option cap admits 11 (surveys-core recipe.rs OptionCount)
// const NPS_11: ScalePreset = {
//     key: "nps-11",
//     label: "NPS (0–10)",
//     options: Array.from({ length: 11 }, (_, n) => ({ key: `nps-${n}`, label: String(n), value: n })),
// };

export function presetByKey(key: string): ScalePreset | undefined {
    return SCALE_PRESETS.find((preset) => preset.key === key);
}

/** True when the options are exactly a preset's expansion (so the editor can
 * show the preset as selected instead of "custom"). */
export function matchingPreset(options: ChoiceOption[]): ScalePreset | undefined {
    return SCALE_PRESETS.find(
        (preset) =>
            preset.options.length === options.length &&
            preset.options.every((expected, i) => {
                const actual = options[i];
                return (
                    actual !== undefined &&
                    expected.key === actual.key &&
                    (expected.icon ?? null) === (actual.icon ?? null) &&
                    (expected.label ?? null) === (actual.label ?? null) &&
                    expected.value === actual.value
                );
            }),
    );
}
